import { useState, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ChevronRight, Search, Users } from 'lucide-react';
import { useForecastTree } from '../hooks/useForecastTree.js';
import { useIndexedExpenses } from '../hooks/useIndexedExpenses.js';
import { useRevenueHypothesis } from '../hooks/useRevenueHypothesis.js';
import { useClientInvoices } from '../hooks/useClientInvoices.js';
import { useEmployees } from '../hooks/useEmployees.js';
import { hasAnyForecast } from '../lib/forecastHelpers.js';
import { formatCurrency } from '../lib/format.js';
import { FORECAST_TYPE_FILTERS, filterForecastCategories, collectSectionIds } from '../lib/forecastFilters.js';
import { useEntityScope } from '../hooks/useEntityScope.js';
import PendingRecurrencesBanner from '../components/forecasts/PendingRecurrencesBanner.jsx';
import PendingRecurrencesPanel from '../components/forecasts/PendingRecurrencesPanel.jsx';
import ForecastCategoryCard from '../components/forecasts/ForecastCategoryCard.jsx';
import ForecastCategorySection from '../components/forecasts/ForecastCategorySection.jsx';
import ForecastAddModal from '../components/forecasts/ForecastAddModal.jsx';
import ProvisionDetailDrawer from '../components/forecasts/ProvisionDetailDrawer.jsx';
import RecurrenceTransactionsPanel from '../components/RecurrenceTransactionsPanel.jsx';
import RevenueHypothesisCard from '../components/forecasts/RevenueHypothesisCard.jsx';

// ----- Toast minimaliste -----

function Toast({ message, type = 'success', onDismiss }) {
  const bg = type === 'error' ? 'bg-red-600' : 'bg-slate-800';
  return (
    <div
      className={`fixed bottom-20 left-1/2 -translate-x-1/2 z-[60] ${bg} text-white text-sm px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-3 lg:bottom-6`}
      role="status"
    >
      <span>{message}</span>
      <button
        onClick={onDismiss}
        className="text-white/60 hover:text-white ml-1 text-lg leading-none focus-visible:outline-none"
        aria-label="Fermer la notification"
      >
        ×
      </button>
    </div>
  );
}

// ----- Skeletons -----

function CardSkeleton() {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden animate-pulse">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
        <div className="flex-1 h-4 rounded bg-slate-200" />
        <div className="w-16 h-4 rounded bg-slate-200 shrink-0" />
        <div className="w-5 h-5 rounded bg-slate-200 shrink-0" />
      </div>
      <div className="px-2 py-1.5 space-y-1">
        <div className="h-3.5 rounded bg-slate-100 w-4/5" />
        <div className="h-3.5 rounded bg-slate-100 w-3/5" />
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      {[0, 1].map((s) => (
        <section key={s}>
          <div className="h-4 w-24 rounded bg-slate-200 animate-pulse mb-3" />
          <div className="space-y-2">
            <CardSkeleton />
            <CardSkeleton />
          </div>
        </section>
      ))}
    </div>
  );
}

// ----- Empty state -----

function EmptyState() {
  return (
    <div className="text-center py-16 text-slate-500">
      <p className="text-lg font-semibold text-slate-700 mb-2">Aucun prévisionnel configuré</p>
      <p className="text-sm mb-6">
        Commencez par créer vos catégories de dépenses et recettes.
      </p>
      <a
        href="/depenses/categories"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 transition-colors"
      >
        Gérer les catégories
      </a>
    </div>
  );
}

// ----- Onglets Actifs / Passés -----

const SCOPE_OPTIONS = [
  { value: 'active', label: 'Actifs' },
  { value: 'past', label: 'Passés' },
];

function ScopeToggle({ value, onChange }) {
  return (
    <div
      className="flex rounded-lg border border-slate-200 overflow-hidden text-xs"
      role="group"
      aria-label="Période des prévisionnels"
    >
      {SCOPE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={value === opt.value}
          className={`px-3 py-1.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
            value === opt.value
              ? 'bg-slate-800 text-white'
              : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ----- Carte-lien Factures émises -----

/**
 * Carte-lien vers /factures — affiche le nombre de factures ouvertes.
 * Pas de garde isGroup ici : ForecastsPage est déjà wrappée dans
 * RequiresSingleEntity côté App.jsx, donc invisible en vue groupe.
 */
function ClientInvoicesLinkCard() {
  const { data } = useClientInvoices({ status: 'open' });
  const invoices = Array.isArray(data) ? data : (data?.items ?? data?.data ?? []);
  const count = invoices.length;

  return (
    <Link
      to="/factures"
      className="flex items-center justify-between rounded-xl bg-white border border-slate-200 shadow-sm px-4 py-3 hover:bg-slate-50 transition-colors group"
      aria-label="Voir les factures émises"
    >
      <div className="flex items-center gap-3">
        <FileText size={18} className="text-slate-400 group-hover:text-slate-600 transition-colors" aria-hidden />
        <div>
          <p className="text-sm font-semibold text-slate-800">Factures émises</p>
          <p className="text-xs text-slate-500">{count} en attente</p>
        </div>
      </div>
      <ChevronRight size={16} className="text-slate-400 group-hover:text-slate-600 transition-colors" aria-hidden />
    </Link>
  );
}

// ----- Carte-lien Employés -----

/**
 * Carte-lien vers /employes — effectif actif et coût mensuel chargé.
 * Les salaires/charges apparaissent en récurrences lecture seule dans l'arbre :
 * cette carte est le point d'entrée pour les modifier.
 */
function EmployeesLinkCard() {
  const { data } = useEmployees({ status: 'active' });
  const employees = Array.isArray(data) ? data : (data?.items ?? data?.data ?? []);
  const monthlyCost = employees.reduce((sum, e) => sum + (e.monthly_cost_cents ?? 0), 0);

  return (
    <Link
      to="/employes"
      className="flex items-center justify-between rounded-xl bg-white border border-slate-200 shadow-sm px-4 py-3 hover:bg-slate-50 transition-colors group"
      aria-label="Voir les employés"
    >
      <div className="flex items-center gap-3">
        <Users size={18} className="text-slate-400 group-hover:text-slate-600 transition-colors" aria-hidden />
        <div>
          <p className="text-sm font-semibold text-slate-800">Employés</p>
          <p className="text-xs text-slate-500">
            {employees.length} actif{employees.length > 1 ? 's' : ''} · coût {formatCurrency(monthlyCost)}/mois
          </p>
        </div>
      </div>
      <ChevronRight size={16} className="text-slate-400 group-hover:text-slate-600 transition-colors" aria-hidden />
    </Link>
  );
}

// ----- Helpers -----

/**
 * Retourne les catégories racines d'un flow_type donné depuis le tableau flows.
 */
function getRootsByFlowType(flows, flowType) {
  if (!Array.isArray(flows)) return [];
  const flow = flows.find((f) => f.flow_type === flowType);
  return flow?.categories ?? [];
}

// Enrichit récursivement une catégorie avec ses indexed-expenses (et celles
// de tous ses descendants). Crée un nouvel objet immutable. Sans la récursion,
// un indexed créé sur une sous-cat Qonto était stocké en DB mais invisible.
function enrichCategoryWithIndexed(cat, indexedByCategory) {
  const indexed = indexedByCategory?.[cat.id] ?? [];
  const indexedItems = indexed.map((ie) => ({ ...ie, mode: 'indexed' }));
  const enrichedChildren = (cat.children ?? []).map((child) =>
    enrichCategoryWithIndexed(child, indexedByCategory)
  );
  return {
    ...cat,
    items: indexedItems.length
      ? [...(cat.items ?? []), ...indexedItems]
      : (cat.items ?? []),
    children: enrichedChildren,
  };
}

/**
 * Groupe interne : décide d'afficher ForecastCategorySection (avec enfants)
 * ou ForecastCategoryCard directe (catégorie feuille).
 * `categories` est déjà enrichi (indexed-expenses) et filtré par la page.
 */
function FlowGroup({
  title,
  categories,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onOpenDrawer,
  onOpenRecurrenceHistory,
  collapsedSections,
  onToggleSection,
  showHypothesisCard,
}) {
  if (categories.length === 0 && !showHypothesisCard) return null;

  const sharedProps = {
    onAddItem, onEditItem, onDeleteItem, onOpenDrawer, onOpenRecurrenceHistory,
    collapsedSections, onToggleSection,
  };

  return (
    <section>
      <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3 px-1">
        {title}
      </h2>
      <div className="space-y-3">
        {showHypothesisCard && <RevenueHypothesisCard />}
        {categories.map((cat) =>
          (cat.children?.length ?? 0) > 0 ? (
            <ForecastCategorySection
              key={cat.id}
              category={cat}
              {...sharedProps}
            />
          ) : (
            <ForecastCategoryCard
              key={cat.id}
              category={cat}
              {...sharedProps}
            />
          )
        )}
      </div>
    </section>
  );
}

// ----- Page principale -----

const EMPTY_COLLAPSED = {};

export default function ForecastsPage() {
  const [scope, setScope] = useState('active');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const { data, isLoading, isError, refetch } = useForecastTree({ scope });
  const { data: indexedExpensesData } = useIndexedExpenses({ status: 'active' });
  const { data: hypothesis } = useRevenueHypothesis();

  // Extraire flows et pending_recurrences depuis la réponse (objet ou array legacy)
  let flows = [];
  let pendingRecurrences = [];
  if (data) {
    if (data.flows) {
      flows = data.flows;
      pendingRecurrences = data.pending_recurrences ?? [];
    } else if (Array.isArray(data)) {
      // Compatibilité avec l'ancien format array
      flows = data.map((d) => ({ flow_type: d.flow_type, categories: d.categories ?? [] }));
    }
  }

  // État des modals/drawers
  const [addModal, setAddModal] = useState(null);
  // addModal = { categoryId, categoryName, flowType } | null

  const [editModal, setEditModal] = useState(null);
  // editModal = { item } | null

  const [provisionDrawer, setProvisionDrawer] = useState(null);
  // provisionDrawer = { item } | null

  const [pendingPanelOpen, setPendingPanelOpen] = useState(false);

  // Auto-ouvrir le panel pending si l'URL contient #pending et qu'il y a des récurrences
  useEffect(() => {
    if (window.location.hash === '#pending' && pendingRecurrences.length > 0) {
      setPendingPanelOpen(true);
    }
  }, [pendingRecurrences.length]);

  const [recurrenceHistory, setRecurrenceHistory] = useState(null);
  // recurrenceHistory = objet recurrence (avec .id) | null

  // Toast
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Callbacks
  const handleAddItem = useCallback((categoryId, categoryName, flowType) => {
    setAddModal({ categoryId, categoryName, flowType });
  }, []);

  const handleEditItem = useCallback((item) => {
    setEditModal({ item });
  }, []);

  const handleOpenDrawer = useCallback((item) => {
    setProvisionDrawer({ item });
  }, []);

  const handleOpenRecurrenceHistory = useCallback((item) => {
    // item ici est un forecast item mode=recurring ; on construit un objet recurrence minimal
    setRecurrenceHistory({
      id: item.recurrence_id ?? item.id,
      label_sample: item.name ?? item.label,
      label_pattern: item.name ?? item.label,
      amount_cents: item.amount_cents_ttc ?? item.amount_cents,
      frequency: item.frequency,
    });
  }, []);

  const handleAddClose = useCallback(() => setAddModal(null), []);
  const handleEditClose = useCallback(() => setEditModal(null), []);
  const handleDrawerClose = useCallback(() => setProvisionDrawer(null), []);
  const handleRecurrenceHistoryClose = useCallback(() => setRecurrenceHistory(null), []);

  const expense = getRootsByFlowType(flows, 'expense');
  const income = getRootsByFlowType(flows, 'income');
  const isEmpty = !isLoading && !isError && expense.length === 0 && income.length === 0;

  // Option A : regrouper les indexed-expenses par category_id pour injection manuelle
  // Le tableau renvoyé par l'API peut être un tableau direct ou { items: [...] }
  const indexedExpensesByCategory = useMemo(() => {
    const list = Array.isArray(indexedExpensesData)
      ? indexedExpensesData
      : (indexedExpensesData?.items ?? []);
    return list.reduce((acc, ie) => {
      if (!acc[ie.category_id]) acc[ie.category_id] = [];
      acc[ie.category_id].push(ie);
      return acc;
    }, {});
  }, [indexedExpensesData]);

  // Sections repliées, mémorisées par entité. localStorage peut lever
  // (mode privé, quota) : chaque accès est isolé pour ne jamais casser la page.
  const { activeEntityId } = useEntityScope();
  const storageKey = activeEntityId ? `forecasts.collapsed.${activeEntityId}` : null;
  const [collapsedSections, setCollapsedSections] = useState(EMPTY_COLLAPSED);

  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      setCollapsedSections(raw ? JSON.parse(raw) : EMPTY_COLLAPSED);
    } catch {
      setCollapsedSections(EMPTY_COLLAPSED);
    }
  }, [storageKey]);

  const persistCollapsed = useCallback((next) => {
    setCollapsedSections(next);
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // pas de persistance disponible — l'état reste en mémoire
    }
  }, [storageKey]);

  // Pendant une recherche, toutes les sections sont affichées dépliées : replier
  // n'aurait aucun effet visible mais écrirait quand même l'état mémorisé.
  const isSearching = search.trim() !== '';

  const handleToggleSection = useCallback((categoryId) => {
    if (isSearching) return;
    const next = { ...collapsedSections };
    if (next[categoryId]) delete next[categoryId];
    else next[categoryId] = true;
    persistCollapsed(next);
  }, [collapsedSections, persistCollapsed, isSearching]);

  const isFiltering = isSearching || typeFilter !== 'all';

  const visibleExpense = useMemo(
    () => filterForecastCategories(
      expense.map((cat) => enrichCategoryWithIndexed(cat, indexedExpensesByCategory)),
      { search, mode: typeFilter }
    ).filter(hasAnyForecast),
    [expense, indexedExpensesByCategory, search, typeFilter]
  );

  const visibleIncome = useMemo(
    () => filterForecastCategories(
      income.map((cat) => enrichCategoryWithIndexed(cat, indexedExpensesByCategory)),
      { search, mode: typeFilter }
    ).filter(hasAnyForecast),
    [income, indexedExpensesByCategory, search, typeFilter]
  );

  const sectionIds = useMemo(
    () => collectSectionIds([...visibleExpense, ...visibleIncome]),
    [visibleExpense, visibleIncome]
  );
  const allCollapsed = sectionIds.length > 0 && sectionIds.every((id) => collapsedSections[id]);

  const handleToggleAllSections = useCallback(() => {
    if (isSearching) return;
    persistCollapsed(allCollapsed
      ? EMPTY_COLLAPSED
      : Object.fromEntries(sectionIds.map((id) => [id, true])));
  }, [allCollapsed, sectionIds, persistCollapsed, isSearching]);

  const effectiveCollapsed = isSearching ? EMPTY_COLLAPSED : collapsedSections;

  const isPast = scope === 'past';

  const sharedCallbacks = {
    onAddItem: isPast ? undefined : handleAddItem,
    onEditItem: handleEditItem,
    onOpenDrawer: handleOpenDrawer,
    onOpenRecurrenceHistory: handleOpenRecurrenceHistory,
    collapsedSections: effectiveCollapsed,
    onToggleSection: handleToggleSection,
  };

  return (
    <div className="py-2 pb-24 lg:pb-6">
      {/* Titre + bouton ajout global */}
      <div className="flex items-center gap-3 mb-4">
        <h1 className="flex-1 text-xl font-bold text-slate-900">Prévisionnels</h1>
        {scope === 'active' && (
          <button
            type="button"
            onClick={() => setAddModal({})}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 text-white text-sm font-medium hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          >
            <span aria-hidden>+</span> Ajouter
          </button>
        )}
      </div>

      {/* Onglets Actifs / Passés */}
      <div className="mb-3">
        <ScopeToggle value={scope} onChange={setScope} />
      </div>

      {/* Recherche + chips de type */}
      <div className="mb-4 space-y-2">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un prévisionnel…"
            aria-label="Rechercher un prévisionnel"
            className="w-full min-h-[44px] pl-8 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-x-auto pb-0.5">
            {FORECAST_TYPE_FILTERS.map((chip) => (
              <button
                key={chip.value}
                type="button"
                onClick={() => setTypeFilter(chip.value)}
                aria-pressed={typeFilter === chip.value}
                className={`shrink-0 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
                  typeFilter === chip.value
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleToggleAllSections}
            disabled={isSearching}
            className="shrink-0 text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded disabled:opacity-40 disabled:no-underline disabled:hover:text-slate-500"
          >
            {allCollapsed ? 'Tout déplier' : 'Tout replier'}
          </button>
        </div>
      </div>

      {/* Cartes-liens Factures émises / Employés */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <ClientInvoicesLinkCard />
        <EmployeesLinkCard />
      </div>

      {/* Chargement */}
      {isLoading && <LoadingSkeleton />}

      {/* Erreur */}
      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm font-medium text-red-700 mb-3">
            Impossible de charger les prévisionnels.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg bg-red-700 text-white text-sm font-medium hover:bg-red-800 transition-colors"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Empty state */}
      {isEmpty && <EmptyState />}

      {/* Contenu chargé */}
      {!isLoading && !isError && !isEmpty && (
        <>
          {/* Bandeau récurrences pending (masqué en scope=past) */}
          {!isPast && pendingRecurrences.length > 0 && (
            <PendingRecurrencesBanner
              count={pendingRecurrences.length}
              onOpen={() => setPendingPanelOpen(true)}
            />
          )}

          {/* Sections flux */}
          <div className="space-y-6">
            <FlowGroup
              title="Dépenses"
              categories={visibleExpense}
              showHypothesisCard={false}
              {...sharedCallbacks}
            />
            <FlowGroup
              title="Revenus"
              categories={visibleIncome}
              showHypothesisCard={!isPast && !isFiltering}
              {...sharedCallbacks}
            />
          </div>
        </>
      )}

      {/* Modal ajout */}
      {addModal && (
        <ForecastAddModal
          isOpen
          onClose={handleAddClose}
          categoryId={addModal.categoryId}
          categoryName={addModal.categoryName}
          categoryFlowType={addModal.flowType}
        />
      )}

      {/* Modal édition */}
      {editModal && (
        <ForecastAddModal
          isOpen
          onClose={handleEditClose}
          categoryId={editModal.item.category_id}
          categoryName={editModal.item.category_name ?? ''}
          categoryFlowType={editModal.item.flow_type ?? 'expense'}
          editItem={editModal.item}
        />
      )}

      {/* Drawer provision */}
      {provisionDrawer && (
        <ProvisionDetailDrawer
          isOpen
          onClose={handleDrawerClose}
          provisionId={provisionDrawer.item.id}
          provisionItem={provisionDrawer.item}
        />
      )}

      {/* Panel récurrences pending */}
      <PendingRecurrencesPanel
        isOpen={pendingPanelOpen}
        onClose={() => setPendingPanelOpen(false)}
        pendingRecurrences={pendingRecurrences}
      />

      {/* Panel historique paiements récurrence */}
      <RecurrenceTransactionsPanel
        open={Boolean(recurrenceHistory)}
        onClose={handleRecurrenceHistoryClose}
        recurrence={recurrenceHistory}
      />

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}
